import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  Boxes,
  Globe2,
  ChevronLeft,
  ChevronRight,
  KeyRound,
  LayoutDashboard,
  LogOut,
  MailCheck,
  Menu,
  Settings2,
  UserRound,
  X,
  Archive,
} from 'lucide-react';
import { api, ApiError, setAdminKey } from './api';
import { Toasts, type ToastState } from './components/Common';
import { OverviewScreen } from './screens/OverviewScreen';
import { MailboxesScreen } from './screens/MailboxesScreen';
import { DomainMailScreen } from './screens/DomainMailScreen';
import { ExpiredMailboxesScreen } from './screens/ExpiredMailboxesScreen';
import { PersonalScreen } from './screens/PersonalScreen';

type PageKey = 'overview' | 'mailboxes' | 'domain-mail' | 'expired' | 'personal';

const navigation = [
  { key: 'overview' as const, label: '概览', icon: LayoutDashboard },
  { key: 'mailboxes' as const, label: '邮箱管理', icon: Boxes },
  { key: 'domain-mail' as const, label: '域名邮箱管理', icon: Globe2 },
  { key: 'expired' as const, label: '失效邮箱池', icon: Archive },
  { key: 'personal' as const, label: '个人中心', icon: UserRound },
];

/**
 * 功能：渲染管理员密钥登录界面，并验证密钥可访问概览接口。
 * 参数：onSuccess（验证成功回调）。
 */
function AuthGate({ onSuccess }: { onSuccess: () => void }) {
  const [key, setKey] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  /** 功能：验证管理员密钥并进入控制台；参数：event（表单提交事件）。 */
  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setAdminKey(key.trim());
    try {
      await api('/api/overview');
      onSuccess();
    } catch (reason) {
      setAdminKey('');
      setError(reason instanceof Error ? reason.message : '密钥验证失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="auth-shell">
      <div className="auth-brand"><MailCheck size={24} /><strong>smartMail</strong><span>SERVER CONSOLE</span></div>
      <form className="auth-panel" onSubmit={submit}>
        <div className="eyebrow"><span />ADMIN ACCESS</div>
        <h1>连接邮箱资源控制台</h1>
        <p>请输入服务器管理密钥。</p>
        <label>管理密钥<input type="password" value={key} onChange={(event) => setKey(event.target.value)} autoFocus autoComplete="current-password" placeholder="sm_admin_••••••••" /></label>
        {error ? <div className="form-error">{error}</div> : null}
        <button className="button button-primary auth-submit" disabled={!key.trim() || loading}><KeyRound size={17} />{loading ? '正在验证' : '进入 smartMail'}</button>
      </form>
      <div className="auth-meta"><span>ENCRYPTED AT REST</span><span>POSTGRESQL / POOL</span><span>API V2</span></div>
    </main>
  );
}

/**
 * 功能：渲染 smartMail 主应用外壳、侧栏导航和页面切换。
 * 参数：无。
 */
export default function App() {
  const [page, setPage] = useState<PageKey>('overview');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [authState, setAuthState] = useState<'loading' | 'ready' | 'required'>('loading');
  const [runtime, setRuntime] = useState({ version: '2.0.0', database: 'postgres' });
  const [toasts, setToasts] = useState<ToastState[]>([]);

  useEffect(() => {
    api<{ authRequired: boolean; version: string; database: string }>('/api/bootstrap')
      .then(async (bootstrap) => {
        setRuntime({ version: bootstrap.version, database: bootstrap.database });
        if (!bootstrap.authRequired) { setAuthState('ready'); return; }
        try { await api('/api/overview'); setAuthState('ready'); }
        catch (error) { setAuthState(error instanceof ApiError && error.status === 401 ? 'required' : 'required'); }
      })
      .catch(() => setAuthState('required'));
  }, []);

  /** 功能：新增短时通知并自动清除；参数：message（通知文本）；type（成功或错误）。 */
  const notify = useCallback((message: string, type: 'success' | 'error' = 'success'): void => {
    const id = Date.now() + Math.random();
    setToasts((current) => [...current, { id, message, type }]);
    window.setTimeout(() => setToasts((current) => current.filter((toast) => toast.id !== id)), 3600);
  }, []);

  /** 功能：切换业务页面并在移动端关闭菜单；参数：nextPage（目标页面标识）。 */
  const navigate = (nextPage: PageKey): void => {
    setPage(nextPage);
    setMobileMenu(false);
  };

  /** 功能：清除会话管理密钥并返回登录页；参数：无。 */
  const logout = (): void => {
    setAdminKey('');
    setAuthState('required');
  };

  const content = useMemo(() => {
    if (page === 'overview') return <OverviewScreen notify={notify} />;
    if (page === 'mailboxes') return <MailboxesScreen notify={notify} />;
    if (page === 'domain-mail') return <DomainMailScreen notify={notify} />;
    if (page === 'expired') return <ExpiredMailboxesScreen notify={notify} />;
    return <PersonalScreen notify={notify} />;
  }, [page, notify]);

  if (authState === 'loading') return <div className="boot-screen"><MailCheck size={28} /><span>smartMail</span></div>;
  if (authState === 'required') return <AuthGate onSuccess={() => setAuthState('ready')} />;

  return (
    <div className={`app-shell ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <aside className={`sidebar ${mobileMenu ? 'sidebar-mobile-open' : ''}`}>
        <div className="brand-block">
          <div className="brand-mark"><MailCheck size={19} strokeWidth={2} /></div>
          <div className="brand-copy"><strong>smartMail</strong><span>MAIL INFRASTRUCTURE</span></div>
          <button className="mobile-close" onClick={() => setMobileMenu(false)} aria-label="关闭菜单"><X size={20} /></button>
        </div>
        <div className="system-strip"><span className="live-dot" /> <span>服务在线</span><code>v{runtime.version.split('.').slice(0, 2).join('.')} / {runtime.database === 'postgres' ? 'PG' : 'SQLITE'}</code></div>
        <nav className="main-nav" aria-label="主导航">
          <div className="nav-label">WORKSPACE</div>
          {navigation.map((item, index) => {
            const Icon = item.icon;
            return (
              <button key={item.key} className={page === item.key ? 'active' : ''} onClick={() => navigate(item.key)} title={sidebarCollapsed ? item.label : undefined}>
                <span className="nav-index">0{index + 1}</span><Icon size={18} /><span className="nav-text">{item.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="sidebar-proof">
          <Activity size={16} />
          <div><strong>池化模型</strong><span>跨平台邮箱可复用</span></div>
        </div>
        <div className="sidebar-footer">
          <button onClick={() => navigate('personal')}><Settings2 size={17} /><span className="nav-text">系统设置</span></button>
          <button onClick={logout}><LogOut size={17} /><span className="nav-text">退出会话</span></button>
        </div>
      </aside>

      {mobileMenu ? <button className="mobile-overlay" aria-label="关闭菜单遮罩" onClick={() => setMobileMenu(false)} /> : null}

      <section className="workspace">
        <div className="topbar">
          <button className="mobile-menu" onClick={() => setMobileMenu(true)} aria-label="打开菜单"><Menu size={20} /></button>
          <button className="collapse-control" onClick={() => setSidebarCollapsed((value) => !value)} aria-label={sidebarCollapsed ? '展开侧栏' : '收起侧栏'} title={sidebarCollapsed ? '展开侧栏' : '收起侧栏'}>
            {sidebarCollapsed ? <ChevronRight size={17} /> : <ChevronLeft size={17} />}
          </button>
          <div className="topbar-context"><span>SMARTMAIL / {navigation.find((item) => item.key === page)?.label.toUpperCase()}</span></div>
          <div className="topbar-status"><span className="live-dot" /> API READY</div>
        </div>
        <div className="content-area">{content}</div>
      </section>
      <Toasts toasts={toasts} onDismiss={(id) => setToasts((current) => current.filter((toast) => toast.id !== id))} />
    </div>
  );
}

